import { simulateLatency, type PaginationParams, type ApiContext } from '../shared';
import { STUB_CREDENTIALS } from './stubData';
import type {
  Credential,
  CreateCredentialRequest,
  UpdateCredentialRequest,
  ListCredentialsResponse,
  RotateCredentialValuesRequest,
  RotateCredentialValuesResponse,
  BatchRotateCredentialsRequest,
  BatchRotateCredentialsResponse,
  UploadCredentialFileRequest,
  UploadCredentialFileResponse,
} from './types';
import { apiClient } from '@admin-ui-root/src/app/lib/apiClient';
import { fileDeliveryApiUrl } from '@admin-ui-root/src/app/pages/FileDelivery/api/api';

export const listCredentials = async (ctx: ApiContext, params?: PaginationParams): Promise<ListCredentialsResponse> => {
  const api = apiClient();

  const searchParams = new URLSearchParams();
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  if (params?.pageToken) searchParams.append('pageToken', params.pageToken);
  if (params?.filter) searchParams.append('filter', params.filter);
  if (params?.orderBy) searchParams.append('orderBy', params.orderBy);

  const queryString = searchParams.toString();
  const url = queryString ? `credentials?${queryString}` : 'credentials';

  const response = await api
    .get(fileDeliveryApiUrl(url), {
      headers: {
        'x-fds-profile': ctx.serviceAccount,
        'x-c2fo-dc': ctx.datacenter,
      },
    })
    .json<unknown>();

  if (!response || typeof response !== 'object' || !('credentials' in response)) {
    return {
      credentials: [],
      totalSize: 0,
    };
  }

  const typedResponse = response as ListCredentialsResponse;
  return {
    credentials: typedResponse.credentials || [],
    nextPageToken: typedResponse.nextPageToken,
    totalSize: typedResponse.totalSize || 0,
  };
};

export const getCredential = async (ctx: ApiContext, name: string): Promise<Credential | null> => {
  const api = apiClient();

  try {
    const response = await api
      .get(fileDeliveryApiUrl(name), {
        headers: {
          'x-fds-profile': ctx.serviceAccount,
          'x-c2fo-dc': ctx.datacenter,
        },
      })
      .json<{ credential: Credential }>();

    return response.credential;
  } catch (error) {
    return null;
  }
};

export const createCredential = async (_ctx: ApiContext, _request: CreateCredentialRequest): Promise<Credential> => {
  // TODO: Replace with real API call when available
  // No-op implementation - returns a mock created credential
  await simulateLatency();

  const newCredential: Credential = {
    ..._request.credential,
    name: `credentials/${crypto.randomUUID()}`,
    uid: crypto.randomUUID(),
    maskedValues: Object.fromEntries(Object.keys(_request.credential.values ?? {}).map((key) => [key, '********'])),
    createTime: new Date().toISOString(),
    updateTime: new Date().toISOString(),
  };

  return newCredential;
};

export const updateCredential = async (_ctx: ApiContext, _request: UpdateCredentialRequest): Promise<Credential> => {
  // TODO: Replace with real API call when available
  // No-op implementation - returns the existing credential with updated fields
  await simulateLatency();

  const existing = STUB_CREDENTIALS.find((cred) => cred.name === _request.credential.name);
  if (!existing) {
    throw new Error(`Credential not found: ${_request.credential.name}`);
  }

  return {
    ...existing,
    ..._request.credential,
    updateTime: new Date().toISOString(),
  };
};

export const deleteCredential = async (_ctx: ApiContext, _name: string, _force?: boolean): Promise<void> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();
};

export const rotateCredentialValues = async (
  ctx: ApiContext,
  _request: RotateCredentialValuesRequest,
): Promise<RotateCredentialValuesResponse> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();

  const existing = STUB_CREDENTIALS.find((cred) => cred.name === _request.name);
  if (!existing) {
    throw new Error(`Credential not found: ${_request.name}`);
  }

  return {
    credential: {
      ...existing,
      updateTime: new Date().toISOString(),
    },
    rotatedKeys: Object.keys(_request.newValues),
  };
};

export const batchRotateCredentials = async (
  _ctx: ApiContext,
  _request: BatchRotateCredentialsRequest,
): Promise<BatchRotateCredentialsResponse> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();

  return {
    results: [],
    totalMatches: 0,
    successfulCount: 0,
    failedCount: 0,
  };
};

/**
 * Upload a file for a credential (e.g., SSH private key, known_hosts, GCP service account JSON).
 * The file content is sent as base64-encoded string.
 * Returns the server-side file path to be used in credential values.
 *
 * @param ctx - API context with datacenter and service account
 * @param request - Upload request with credential name, filename, and base64 content
 * @returns The file path where the file was stored on the server
 *
 * @example
 * // Upload an SSH private key
 * const result = await uploadCredentialFile(ctx, {
 *   name: 'credentials/abc-123',
 *   filename: 'id_rsa',
 *   content: btoa(privateKeyContent),
 * });
 * // Use result.filePath as the value for 'keyFilePath' in credential values
 */
export const uploadCredentialFile = async (
  ctx: ApiContext,
  request: UploadCredentialFileRequest,
): Promise<UploadCredentialFileResponse> => {
  // TODO: Replace with real API call when available
  // Real implementation will POST to /v2/credentials:uploadFile
  // with JSON body: { name, filename, content }
  await simulateLatency();

  // Mock response: generate a fake file path based on credential name and filename
  const credentialId = request.name.replace('credentials/', '');
  const filePath = `/var/lib/file-delivery-service/credentials/${credentialId}/${request.filename}`;

  return {
    filePath,
  };
};

/**
 * Helper function to convert a File object to base64 string.
 * Used when uploading files from the browser.
 *
 * @param file - File object from file input or drag-and-drop
 * @returns Promise resolving to base64-encoded file content
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:text/plain;base64,")
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Helper function to convert a string to base64.
 * Used when the user pastes content directly in a text area.
 *
 * @param content - Plain text content
 * @returns Base64-encoded string
 */
export const stringToBase64 = (content: string): string => {
  return btoa(content);
};
